import clsx from "clsx";
import { BACKEND } from "core/consts";
import { tokenAccessor } from "core/model/api/transport";
import { getBackendConfig } from "core/model/config";
import { Button, Spinner } from "ds/ui";
import {
  Component,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Document, Page } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { useTranslations } from "translations";
import { setupPdfWorker } from "./pdfWorker";

setupPdfWorker();

class PdfDocumentErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    console.error("PdfViewer render error:", error.message);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

export type PdfViewerFile = {
  fileName: string;
  isDeleted?: boolean;
  url: string;
  uploadedAt?: number;
  id: string | number;
};

const useContainerWidth = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const widthRef = useRef<number>(0);
  const isInitialMountRef = useRef(true);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const measureWidth = () => {
      const width = element.clientWidth;
      if (width > 0) {
        if (isInitialMountRef.current) {
          widthRef.current = width;
          setContainerWidth(width);
          isInitialMountRef.current = false;
        } else if (Math.abs(width - widthRef.current) > 1) {
          widthRef.current = width;
          setContainerWidth(width);
        }
      }
    };

    measureWidth();
  }, []);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    let frameId = 0;
    const resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (width > 0 && Math.abs(width - widthRef.current) > 1) {
        cancelAnimationFrame(frameId);
        frameId = requestAnimationFrame(() => {
          widthRef.current = width;
          setContainerWidth(width);
        });
      }
    });

    resizeObserver.observe(element);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
    };
  }, []);

  return {
    containerRef,
    containerWidth,
  };
};

export const PdfViewer = ({
  isLoadingPdf,
  selectedFile,
}: {
  selectedFile: PdfViewerFile;
  isLoadingPdf?: boolean;
}) => {
  const translations = useTranslations();
  const aiCoreConfig = getBackendConfig(BACKEND.AI_CORE);
  const token = tokenAccessor();

  const [loadedPdf, setLoadedPdf] = useState<{
    numPages: number;
    url: string;
  } | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const { containerRef, containerWidth } = useContainerWidth();

  const memoizedFile = useMemo(() => {
    const isAiCoreHostedFile =
      aiCoreConfig?.host && selectedFile?.url?.includes(aiCoreConfig?.host);

    if (isAiCoreHostedFile) {
      return {
        httpHeaders: token ? { Authorization: `Bearer ${token}` } : undefined,
        url: selectedFile.url,
      };
    }
    return {
      url: selectedFile.url,
    };
  }, [selectedFile.url, aiCoreConfig?.host]);

  const currentUrlRef = useRef(memoizedFile.url);
  currentUrlRef.current = memoizedFile.url;

  const handleError = (err: Error) => {
    console.error("PDF load error:", err.message);
  };

  const pageWidth = Math.floor(containerWidth);
  const isLoading = !!isLoadingPdf || !loadedPdf?.numPages;

  if (!memoizedFile.url) return null;

  return (
    <div
      ref={containerRef}
      className={clsx(
        "flex min-h-0 w-full flex-1 overflow-y-auto rounded-[2px] border shadow-sm",
        {
          "border-gray-400": !isLoading,
          "border-transparent": isLoading,
          "items-center justify-center": isLoading,
        },
      )}
      data-testid="pdf-viewer"
      style={{ scrollbarGutter: "stable" }}
    >
      {isLoadingPdf ? (
        <div className="flex h-full w-full flex-1 items-center justify-center">
          <Spinner
            color="primary"
            size="lg"
            testId="spinner-loading-pdf-document"
          />
        </div>
      ) : (
        <PdfDocumentErrorBoundary
          key={retryKey}
          fallback={
            <div className="flex h-full w-full flex-1 flex-col items-center justify-center gap-4">
              <p className="text-body-md text-gray-600">
                {translations.extractDocs.docsCreation.errorPdfLoadingFailed}
              </p>
              <Button
                color="primary"
                id="retry-pdf"
                size="sm"
                variant="ghost"
                onPress={() => setRetryKey((k) => k + 1)}
              >
                {translations.general.tryAgain}
              </Button>
            </div>
          }
        >
          <Document
            className="flex min-h-0 w-full flex-1 flex-col"
            error={
              <p>
                {translations.extractDocs.docsCreation.errorPdfLoadingFailed}
              </p>
            }
            file={memoizedFile}
            loading={
              <div className="flex h-full min-h-[240px] w-full flex-1 items-center justify-center">
                <Spinner
                  color="primary"
                  size="lg"
                  testId="spinner-loading-pdf-document"
                />
              </div>
            }
            onLoadError={handleError}
            onLoadSuccess={(info) => {
              setLoadedPdf({
                numPages: info.numPages,
                url: currentUrlRef.current,
              });
            }}
            onSourceError={handleError}
          >
            {pageWidth > 0 &&
              loadedPdf !== null &&
              loadedPdf.url === memoizedFile.url &&
              Array.from({ length: loadedPdf.numPages }, (_, i) => (
                <Page
                  key={i}
                  error={null}
                  loading={null}
                  pageNumber={i + 1}
                  renderAnnotationLayer
                  renderForms
                  renderTextLayer={false}
                  width={pageWidth}
                />
              ))}
          </Document>
        </PdfDocumentErrorBoundary>
      )}
    </div>
  );
};
